import {
	faCalendarDays as fasCalendarDays,
	faHouse as fasHouse,
	faPlay as fasPlay,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { Config } from "@/app/config";
import type { AbstractSubmitStepProps } from "@/component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { AbstractSubmitStep } from "@/component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { CONFIG } from "@/component/Mortgage/Calculator/data";
import { FormCurrency, FormDecidable, FormNumber, ProgressButton } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import { DEFAULT_PARAMS } from "../data";

export interface StepFields {
	loanAmount: number;
	realEstateValue: number;
	maturity: number;
	income: number;
	eligibleForHomeStart: boolean;
}

export interface Props extends AbstractSubmitStepProps {
	variant?: string;
}

export class Step1 extends AbstractSubmitStep<StepFields, Props> {
	protected initValues(): StepFields {
		const { loanAmount, maturity, realEstateValue, income, eligibleForHomeStart } = DEFAULT_PARAMS;

		return {
			loanAmount,
			realEstateValue,
			maturity,
			income,
			eligibleForHomeStart,
		};
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { submitState, variant } = this.props;
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Header className="bg-secondary-gradient text-white text-center pb-1 pt-3">
					<div className="display-3 text-uppercase">
						<strong>{variant === "freeUse" ? "Jelzáloghitel" : "Lakáshitel"} kalkulátor</strong>
					</div>
					<div className="display-6 text-uppercase">
						<b>Spórolj akár Milliókat</b>
					</div>
				</Card.Header>
				<Card.Body>
					<FormCurrency
						label="Ingatlan értéke"
						name="realEstateValue"
						prepend={<FontAwesomeIcon icon={fasHouse} />}
						infoTooltip="A hitel fedezetéül szolgáló ingatlan piaci értéke. Otthon Start és CSOK Plusz esetében van vételárkorlát, mely felett a kérdéses támogatást már nem lehet kihasználni. (Fő szabály szerint első lakásszerzőknél a lakás ára 100 millió Ft-ot, míg a ház ára a 150 millió Ft-ot nem létpheti át.)"
						validate={this.validRealEstateValue}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Hitelösszeg"
						name="loanAmount"
						infoTooltip="Mekkora hitelt szeretnél felvenni?"
						validate={this.validLoanAmount}
						disabled={disabled}
						required
					/>
					<FormNumber
						label="Futamidő"
						prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
						append="év"
						infoTooltip="Ennyi időn keresztül fogod visszafizetni a hitelt."
						name="maturity"
						validate={this.validMaturity}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Család havi nettó jövedelme"
						name="income"
						infoTooltip="A háztartás több jövedelme is összeadható!"
						validate={this.validIncome}
						disabled={disabled}
						required
					/>
					<FormDecidable
						label="Jogosult vagyok Otthon Start hitelre"
						name="eligibleForHomeStart"
						infoTooltip="Jelenleg, illetve az elmúlt 10 évben nincs, nem volt belterületi lakóingatlanod. Megfelelő, ha olyan ingatlanod/ingatlanjaid vannak/voltak, melyek értéke nem haladja meg a 15 millió Ft-ot, vagy azokat elbontották, vagy ha haszonélvezettel terhelten szerezted őket és ugyanazon haszonélvező jelenleg is bent lakik. Ezek mellett még egyidőben legfeljebb egy belterületi lakóingatlanban lehet 50%-ot elérő tulajdoni hányadod."
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						icon={<FontAwesomeIcon icon={fasPlay} />}
						state={submitState}
						className={`${!form.getState().valid ? "disabled" : ""} text-uppercase font-weight-bold`}
					>
						{variant === "freeUse" ? "Jelzáloghitelek" : "Lakáshitelek"} keresése
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validLoanAmount(loanAmount: number, values: StepFields): string {
		const { qualifiesForHigherLtv } = DEFAULT_PARAMS;

		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < CONFIG.MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(CONFIG.MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > CONFIG.MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(CONFIG.MAX_LOAN_AMOUNT, "ft")}!`;
		}

		const ltv = qualifiesForHigherLtv ? 0.9 : 0.8;
		const realEstateLtvValue = values.realEstateValue * ltv;

		if (loanAmount > realEstateLtvValue) {
			return `A hitelösszeg nem lehet nagyobb az ingatlan értékének ${ltv * 100}%-ánál (${formatText(
				realEstateLtvValue,
				"ft",
			)})!`;
		}
	}

	@autobind
	private validRealEstateValue(realEstateValue: number): string {
		if (realEstateValue < CONFIG.MIN_REAL_ESTATE_VALUE) {
			return `A vásárolni kívánt ingatlan minimális értéke ${formatText(CONFIG.MIN_REAL_ESTATE_VALUE, "ft")}!`;
		}

		if (realEstateValue > CONFIG.MAX_REAL_ESTATE_VALUE) {
			return `A vásárolni kívánt ingatlan maximális értéke ${formatText(CONFIG.MAX_REAL_ESTATE_VALUE, "ft")}!`;
		}
	}

	@autobind
	private validMaturity(maturity: number): string {
		if (isNaN(maturity)) {
			return "A futamidő megadása kötelező!";
		}

		if (maturity < CONFIG.MIN_MATURITY) {
			return `A minimálisan megadható futamidő ${CONFIG.MIN_MATURITY} év!`;
		}

		if (maturity > CONFIG.MAX_MATURITY) {
			return `A maximálisan megadható futamidő ${CONFIG.MAX_MATURITY} év!`;
		}
	}

	@autobind
	private validIncome(income: number): string {
		if (isNaN(income)) {
			return "A jövedelem megadása kötelező!";
		}

		if (income < Config.getMinWage()) {
			return `A megadható minimális összjövedelem ${formatText(
				Config.getMinWage(),
				"ft",
			)}, amely megegyezik az aktuális minimálbérrel.`;
		}

		if (income > CONFIG.INCOME_MAX) {
			return `A megadható maximális jövedelem ${formatText(CONFIG.INCOME_MAX, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
